import { gridFromDragRect, applyHandleDrag } from './grid';

// Three-phase state machine driving the grid editor on top of a
// GridRenderer:
//
//   idle           → no editor active
//   drag-start     → no grid yet; waiting for the first corner click
//   drag-end       → first corner placed; rubber-banding to second corner
//   adjust         → grid exists; user drags intersection handles to tune
//
// The editor doesn't own the persisted grid and doesn't talk to spawn
// config. Callers pass a getGrid() reader; onChange fires as the
// in-flight grid mutates and onCommit fires when accept()/clear() is
// called. GameBoard wires those into its own state + spawnConfig.

export class GridEditor {
  constructor({ renderer, getGrid, onChange, onCommit }) {
    this.renderer = renderer;
    this.getGrid = getGrid;
    this.onChange = onChange || (() => {});
    this.onCommit = onCommit || (() => {});
    this.mode = null;
    this.dragStart = null;
    this.dragEnd = null;
    this.workingGrid = null;
    this.draggingHandle = null;
  }

  isActive() {
    return this.mode !== null;
  }

  start() {
    const existing = this.getGrid();
    if (existing) {
      this.mode = 'adjust';
      this.workingGrid = existing;
      this.renderer.update(existing, { showHandles: true });
    } else {
      this.mode = 'drag-start';
      this.workingGrid = null;
      this.renderer.clearGridGeometry();
    }
  }

  stop() {
    this.mode = null;
    this.dragStart = null;
    this.dragEnd = null;
    this.workingGrid = null;
    this.draggingHandle = null;
    this.renderer.clearGridGeometry();
    this.renderer.clearHighlight();
  }

  // Returns true if the event was consumed by the editor.
  pointerDown(point, pickedHandleMesh = null) {
    if (this.mode === 'drag-start') {
      this.dragStart = { x: point.x, z: point.z };
      this.dragEnd = { x: point.x, z: point.z };
      this.mode = 'drag-end';
      this.renderer.previewRect(this.dragStart, this.dragEnd);
      return true;
    }
    if (this.mode === 'adjust' && pickedHandleMesh) {
      this.draggingHandle = pickedHandleMesh;
      return true;
    }
    return false;
  }

  pointerMove(point) {
    if (this.mode === 'drag-end') {
      this.dragEnd = { x: point.x, z: point.z };
      this.renderer.previewRect(this.dragStart, this.dragEnd);
      return true;
    }
    if (this.mode === 'adjust' && this.draggingHandle) {
      const next = applyHandleDrag(this.workingGrid, this.draggingHandle.userData, point);
      this.workingGrid = next;
      this.renderer.update(next, { showHandles: true });
      this.onChange(next);
      return true;
    }
    return false;
  }

  pointerUp() {
    if (this.mode === 'drag-end') {
      const grid = gridFromDragRect(this.dragStart, this.dragEnd);
      this.workingGrid = grid;
      this.mode = 'adjust';
      this.dragStart = null;
      this.dragEnd = null;
      this.renderer.update(grid, { showHandles: true });
      this.onChange(grid);
      return true;
    }
    if (this.mode === 'adjust' && this.draggingHandle) {
      this.draggingHandle = null;
      return true;
    }
    return false;
  }

  accept() {
    if (!this.workingGrid) {
      this.stop();
      return null;
    }
    const committed = this.workingGrid;
    this.onCommit(committed);
    this.workingGrid = null;
    this.mode = null;
    this.renderer.update(committed, { showHandles: false });
    return committed;
  }

  cancel() {
    this.workingGrid = null;
    this.mode = null;
    this.draggingHandle = null;
    this.dragStart = null;
    this.dragEnd = null;
    const existing = this.getGrid();
    if (existing) this.renderer.update(existing, { showHandles: false });
    else this.renderer.clearGridGeometry();
  }

  clear() {
    this.workingGrid = null;
    this.mode = null;
    this.draggingHandle = null;
    this.dragStart = null;
    this.dragEnd = null;
    this.renderer.clearGridGeometry();
    this.onCommit(null);
  }
}
